/**
 * Theme Purchase Callback
 * Handles redirect from Paystack after payment
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import './ThemePurchaseCallback.css';

// SVG Icons
const CheckCircleIcon = () => (
  <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
    <path d="M22 4L12 14.01l-3-3" />
  </svg>
);

const XCircleIcon = () => (
  <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
);

const LoaderIcon = () => (
  <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="tpc-spinner">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

function ThemePurchaseCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [themeName, setThemeName] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference') || searchParams.get('trxref');

      if (!reference) {
        setStatus('error');
        setMessage('No payment reference found');
        return;
      }

      try {
        const res = await axios.post('/themes/purchase/verify', { reference });

        if (res.data.success) {
          setStatus('success');
          setThemeName(res.data.theme?.name || 'Theme');
          setMessage(`Successfully purchased ${res.data.theme?.name || 'theme'}!`);
          showToast('Theme purchased successfully!', 'success');
        } else {
          setStatus('error');
          setMessage(res.data.error || 'Payment verification failed');
        }
      } catch (error) {
        console.error('[ThemePurchaseCallback] Verification error:', error);
        setStatus('error');
        setMessage(error.response?.data?.error || 'Failed to verify payment');
      }
    };

    verifyPayment();
  }, [searchParams, showToast]);

  const handleContinue = () => {
    // Marketplace was unified into /manage-store/customization (Premium tab).
    navigate('/manage-store/customization');
  };

  return (
    <div className="tpc-container">
      <div className="tpc-card">
        {status === 'verifying' && (
          <>
            <div className="tpc-icon tpc-icon--loading">
              <LoaderIcon />
            </div>
            <h2 className="tpc-title">Verifying Payment</h2>
            <p className="tpc-message">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="tpc-icon tpc-icon--success">
              <CheckCircleIcon />
            </div>
            <h2 className="tpc-title">Payment Successful!</h2>
            <p className="tpc-message">
              You now have access to <strong>{themeName}</strong>.
              You can activate it from the theme marketplace.
            </p>
            <button className="tpc-btn tpc-btn--primary" onClick={handleContinue}>
              Go to Theme Marketplace
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="tpc-icon tpc-icon--error">
              <XCircleIcon />
            </div>
            <h2 className="tpc-title">Payment Failed</h2>
            <p className="tpc-message">{message}</p>
            <div className="tpc-actions">
              <button className="tpc-btn tpc-btn--secondary" onClick={handleContinue}>
                Back to Themes
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ThemePurchaseCallback;
